import { z } from "zod";
/**
 * Contract for real-time in-app notifications delivered over SSE.
 *
 * Unlike the email queue (`email.ts`, SQS), these events never leave Postgres:
 * the API emits one as a `pg_notify` payload on the `notifications` channel
 * inside the same transaction that writes the underlying row, every API task
 * holds a `LISTEN` connection, and the task holding the recipient's SSE
 * connection forwards the event. See `apps/api/src/plugins/notifications.ts`.
 *
 * Keep the payload SMALL — `pg_notify` caps a payload at 8000 bytes, so the
 * envelope carries only routing (`userId`) plus the minimum the client needs to
 * render a toast and refresh a query. Anything heavier is re-fetched over REST.
 *
 * Adding a notification: add a member to the discriminated union below and a
 * matching `addEventListener(<type>, …)` on the client. The `type` discriminant
 * ties producer, SSE frame name, and client handler together.
 */
/** Postgres LISTEN/NOTIFY channel shared by producer and listener — keep in sync. */
export const NOTIFY_CHANNEL = "notifications";
export const notificationEnvelopeSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("submission_received"),
        /** Recipient: `share_links.user_id` (the owner of the link that was filled in). */
        userId: z.string(),
        /** The new `submissions.id`, so the client can target it if needed. */
        submissionId: z.string(),
        /** Owner's pending-submission count after this insert (for toast copy / a badge). */
        pendingCount: z.number().int().nonnegative(),
        /** Visitor's name if they supplied one — display only, capped to bound payload size. */
        visitorName: z.string().max(120).nullable(),
    }),
]);
/** Largest serialized payload we'll hand to pg_notify; under the 8000-byte hard cap. */
const MAX_NOTIFY_BYTES = 7500;
/** UTF-8 byte length, universal across Node and the browser (no Node `Buffer`). */
function byteLength(value) {
    return new TextEncoder().encode(value).length;
}
/**
 * Validate an envelope and serialize it for `pg_notify`.
 *
 * Returns the JSON string, or `null` if it would exceed the payload cap (the
 * variable-length fields are already bounded by the schema, so this is a
 * defensive guard). The caller skips the notify on `null` rather than throwing
 * on the request path — a missed live toast is recoverable via the REST list.
 */
export function serializeNotification(event) {
    const json = JSON.stringify(notificationEnvelopeSchema.parse(event));
    return byteLength(json) > MAX_NOTIFY_BYTES ? null : json;
}
//# sourceMappingURL=notifications.js.map